import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  RefreshCw,
  Send,
  ShieldAlert,
  ShieldX,
} from 'lucide-react';
import type { QuestionOption } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import {
  ModeChip,
  QuestionNavigator,
  QuestionRenderer,
  ScopeChip,
  Timer,
  type AnswerSubmit,
  type AttemptDetail,
  type AttemptStart,
  type BlurResponse,
} from '../components/assessments';

export const Route = createFileRoute('/_authed/assessments/attempts/$attemptId')({
  component: AttemptPage,
});

// Storage key for restoring the current question index across refreshes.
const positionStorageKey = (attemptId: string): string =>
  `bbl-attempt-pos-${attemptId}`;

// Minimal shape we keep in local state to drive the UI. The attempt-detail
// endpoint returns enough to rebuild this on a refresh; the start endpoint
// returns the question pool and shuffled options on first arrival.
interface AttemptViewQuestion {
  id: string;
  position: number;
  stem: string;
  options: QuestionOption[];
  /** Practice-mode running attempt count (how many guesses on this question). */
  attemptCount: number;
  /** Whether this question is finalised (practice-correct or all done). */
  finalized: boolean;
  /** Locally-selected option ids (timed exam only persists; practice clears). */
  selectedOptionIds: string[];
  /** Inline practice-mode feedback to display. */
  feedback: 'correct' | 'wrong' | null;
}

interface AttemptViewState {
  attemptId: string;
  runId: string;
  templateId: string;
  mode: 'timed_exam' | 'practice';
  scope: import('../components/assessments').AssessmentScope | null;
  expiresAt: string | null;
  initialSecondsRemaining: number | null;
  antiCheatStrict: boolean;
  /** Drives the timer's local countdown initial value. */
  questions: AttemptViewQuestion[];
  currentIndex: number;
}

function AttemptPage() {
  const { attemptId } = Route.useParams();
  const navigate = useNavigate();

  // We hydrate the attempt view from EITHER the start endpoint (when we land
  // here from /runs/:id/start) OR from the attempt-detail endpoint (when the
  // user reloads). The attempt-detail endpoint is always our source of truth
  // on remount.
  const detailQuery = useQuery({
    queryKey: ['assessments', 'attempt', attemptId] as const,
    queryFn: ({ signal }) =>
      apiRequest<AttemptDetail>(
        `/api/assessments/attempts/${encodeURIComponent(attemptId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 1;
    },
  });

  // Once we have the detail, if the attempt is terminal, redirect to result.
  //
  // EXCEPTION: a `void` attempt has no finalised score (the void paths — both
  // the L-2 anti-cheat auto-void and the manual HR void — skip scoring), so
  // the generic result page would show the misleading "your assessment has
  // been recorded" copy. We render a dedicated voided panel inline instead.
  // On this fresh-load path we cannot tell WHY the attempt was voided (the
  // candidate detail payload does not expose voidReason), so we use
  // reason-neutral copy. The specific "tab switching" message is reserved for
  // the in-session auto-void, which AttemptRunner proves locally from the blur
  // response.
  useEffect(() => {
    if (!detailQuery.data) return;
    const status = detailQuery.data.status;
    if (status !== 'in_progress' && status !== 'void') {
      void navigate({
        to: '/assessments/attempts/$attemptId/result',
        params: { attemptId },
      });
    }
  }, [detailQuery.data, attemptId, navigate]);

  const status = detailQuery.data?.status;

  return (
    <GatedSection
      error={detailQuery.error}
      title="Test attempt"
      onRetry={() => detailQuery.refetch()}
    >
      {detailQuery.isLoading || !detailQuery.data ? (
        <div className="mx-auto max-w-3xl px-4 py-8">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="mt-4 h-48 w-full" />
        </div>
      ) : status === 'void' ? (
        <VoidedPanel reason="unknown" />
      ) : status !== 'in_progress' ? (
        <div className="mx-auto max-w-3xl px-4 py-8 text-sm text-[color:var(--color-text-muted)]">
          Redirecting to your result…
        </div>
      ) : (
        <AttemptRunner
          attemptId={attemptId}
          detail={detailQuery.data}
        />
      )}
    </GatedSection>
  );
}

// ---------------------------------------------------------------------------
// VoidedPanel — terminal "your test was ended automatically" state (L-2)
// ---------------------------------------------------------------------------

/**
 * Terminal panel for a voided attempt — no score is shown (the void paths skip
 * scoring) and there is no path back into the question UI.
 *
 * - `reason="tab_switch"` is used for the in-session L-2 auto-void, which the
 *   blur response proves was caused by repeated tab/window switching. It gets
 *   the specific deterrent copy.
 * - `reason="unknown"` is used on a fresh load of an already-voided attempt,
 *   where the cause is not exposed to the candidate (it may be the anti-cheat
 *   auto-void OR a manual HR void). It gets reason-neutral copy so the message
 *   is never wrong.
 */
function VoidedPanel({ reason }: { reason: 'tab_switch' | 'unknown' }) {
  const isTabSwitch = reason === 'tab_switch';
  return (
    <div className="mx-auto flex min-h-[calc(100vh-3rem)] max-w-xl flex-col items-center justify-center px-4 py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-[color:var(--color-danger)] bg-[color:var(--color-danger)]/10">
        <ShieldX
          size={32}
          className="text-[color:var(--color-danger)]"
          aria-hidden="true"
        />
      </div>
      <h1 className="mt-6 font-display text-2xl font-bold text-[color:var(--color-text)]">
        {isTabSwitch
          ? 'Your test was ended automatically'
          : 'This attempt has been ended'}
      </h1>
      {isTabSwitch ? (
        <p className="mt-3 text-sm leading-relaxed text-[color:var(--color-text-muted)]">
          We detected repeated switching away from the test window during your
          test. To protect the fairness of the hiring process, this
          attempt has been ended and submitted automatically.
        </p>
      ) : (
        <p className="mt-3 text-sm leading-relaxed text-[color:var(--color-text-muted)]">
          This test attempt has ended and can’t be continued.
        </p>
      )}
      <p className="mt-3 text-sm leading-relaxed text-[color:var(--color-text-muted)]">
        If you believe this was a mistake, please contact the academy&rsquo;s HR
        team — they can review what happened and decide on next steps.
      </p>
      <Link
        to="/me/assessments"
        className="mt-8 inline-flex items-center gap-1 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-2 text-sm font-medium text-[color:var(--color-text)] transition-colors hover:bg-[color:var(--color-bg-elevated)]"
      >
        <ChevronLeft size={16} aria-hidden="true" />
        Back to my tests & quizzes
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AttemptRunner — the actual attempt-taking UI
// ---------------------------------------------------------------------------

interface AttemptRunnerProps {
  attemptId: string;
  detail: AttemptDetail;
}

function AttemptRunner({ attemptId, detail }: AttemptRunnerProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm } = useConfirm();

  // ----- Bootstrap view state from the detail payload ----------------------
  const [view, setView] = useState<AttemptViewState>(() =>
    buildInitialView(attemptId, detail),
  );

  // If the detail re-fetches (e.g. window focus refetch), we rebuild the view
  // but preserve the local selection/feedback so the user doesn't lose their
  // place. We compare answered-state to decide whether to re-hydrate.
  useEffect(() => {
    setView((prev) => mergeDetail(prev, detail));
  }, [detail]);

  const current = view.questions[view.currentIndex];
  const totalQuestions = view.questions.length;
  const answeredSet = useMemo(() => {
    const set = new Set<number>();
    view.questions.forEach((q, i) => {
      if (q.finalized || q.selectedOptionIds.length > 0) set.add(i);
    });
    return set;
  }, [view.questions]);

  // ----- Persist current index across refreshes ---------------------------
  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        positionStorageKey(attemptId),
        String(view.currentIndex),
      );
    } catch {
      // Storage may be unavailable in private mode — ignore.
    }
  }, [attemptId, view.currentIndex]);

  // ----- Submit answer (debounced for timed exam) -------------------------
  const submitMutation = useMutation({
    mutationFn: (args: { questionId: string; selectedOptionIds: string[] }) =>
      apiRequest<AnswerSubmit>(
        `/api/assessments/attempts/${encodeURIComponent(attemptId)}/answers`,
        {
          method: 'POST',
          body: {
            questionId: args.questionId,
            selectedOptionIds: args.selectedOptionIds,
          },
        },
      ),
  });

  // ----- Anti-cheat blur tracking (L-2 enforcement) -----------------------
  //
  // The server is the source of truth: every tab/window switch POSTs to
  // /blur, and the response tells us whether the candidate has been flagged
  // (at the flag threshold) or auto-voided (at the void threshold). We mirror
  // that into local state to drive a PERSISTENT warning banner and, on void,
  // a hard client-side stop.
  // Seed from the detail payload so a candidate who was ALREADY flagged before
  // a reload sees the persistent banner immediately, without waiting for their
  // next blur event. The detail endpoint carries both fields.
  const [integrity, setIntegrity] = useState<{
    flagged: boolean;
    voided: boolean;
    count: number;
  }>(() => ({
    flagged: Boolean(detail.flaggedForReview),
    voided: false,
    count: detail.tabSwitchCount ?? 0,
  }));

  // Ref mirrors so event handlers / debounced callbacks can read the latest
  // values without being re-created when state changes.
  const voidedRef = useRef(false);
  voidedRef.current = integrity.voided;
  const flaggedRef = useRef(integrity.flagged);
  flaggedRef.current = integrity.flagged;
  // One-shot guard for the "you have been flagged" toast — the persistent
  // banner carries the ongoing message, so the toast only fires once, on the
  // first flag→true transition observed in this session (a reload that seeds
  // an already-flagged state shows the banner but does not re-toast).
  const flagToastFiredRef = useRef(false);
  // Cancels any in-flight debounced timed-exam answer save (see debounceRef).
  const cancelPendingSubmitRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!view.antiCheatStrict) return;

    const reportBlur = async (
      kind: 'tab_blur' | 'visibility_hidden' | 'window_blur',
    ) => {
      // Once voided, stop reporting — the attempt is terminal.
      if (voidedRef.current) return;
      try {
        const res = await apiRequest<BlurResponse>(
          `/api/assessments/attempts/${encodeURIComponent(attemptId)}/blur`,
          { method: 'POST', body: { kind } },
        );
        if (res.voided) {
          // Hard stop. Cancel any queued answer save so a late debounce can't
          // fire after the attempt is dead, then flip into the voided state —
          // the runner swaps the question UI for the terminal panel, which
          // unmounts the Timer (stopping its heartbeat) too.
          cancelPendingSubmitRef.current();
          setIntegrity({
            flagged: true,
            voided: true,
            count: res.tabSwitchCount,
          });
          toast(
            'Your test was ended automatically due to repeated tab switching.',
            { variant: 'error' },
          );
          return;
        }
        // Toast once on the first flag→true transition this session. Compute
        // this OUTSIDE the state updater (updaters must stay pure) — the ref
        // mirror gives us the latest flagged value without reading state.
        if (
          res.flaggedForReview &&
          !flaggedRef.current &&
          !flagToastFiredRef.current
        ) {
          flagToastFiredRef.current = true;
          toast(
            'You have been flagged for switching away from the test. Further switching will end your test automatically.',
            { variant: 'error' },
          );
        }
        setIntegrity((prev) => ({
          flagged: prev.flagged || res.flaggedForReview,
          voided: false,
          count: res.tabSwitchCount,
        }));
      } catch {
        // Silent: a failed report is not fatal for the candidate, and the
        // server-side cron / next event will still enforce the thresholds.
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        void reportBlur('visibility_hidden');
      }
    };
    const onBlur = () => {
      void reportBlur('window_blur');
    };
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [attemptId, view.antiCheatStrict, toast]);

  // ----- Practice-mode answer submit (immediate) --------------------------
  const submitPracticeAnswer = useCallback(
    async (qIndex: number, ids: string[]) => {
      // L-2: once auto-voided, refuse to submit anything client-side. The
      // server already rejects non-in_progress attempts; this is the matching
      // client guard so a queued action can't slip through.
      if (voidedRef.current) return;
      const q = view.questions[qIndex];
      if (!q) return;
      try {
        const res = await submitMutation.mutateAsync({
          questionId: q.id,
          selectedOptionIds: ids,
        });
        setView((prev) => {
          const next = [...prev.questions];
          const cur = next[qIndex];
          if (!cur) return prev;
          const updated: AttemptViewQuestion = {
            ...cur,
            attemptCount: res.attemptCount,
            finalized: res.status === 'finalized',
            feedback: res.isCorrect === true ? 'correct' : res.isCorrect === false ? 'wrong' : null,
            // On wrong: server clears selection so the user can retry.
            selectedOptionIds:
              res.isCorrect === true
                ? cur.selectedOptionIds
                : [],
          };
          next[qIndex] = updated;
          return { ...prev, questions: next };
        });
        // If correct, auto-advance after 1.5s.
        if (res.isCorrect === true && qIndex < totalQuestions - 1) {
          window.setTimeout(() => {
            setView((prev) =>
              prev.currentIndex === qIndex
                ? { ...prev, currentIndex: qIndex + 1 }
                : prev,
            );
          }, 1500);
        }
      } catch (err) {
        toast(
          err instanceof ApiError ? err.message : 'Could not submit answer.',
          { variant: 'error' },
        );
      }
    },
    [submitMutation, totalQuestions, toast, view.questions],
  );

  // ----- Timed exam — debounced answer save -------------------------------
  const debounceRef = useRef<number | null>(null);

  // Expose a stable canceller so the anti-cheat void path can drop a queued
  // save before it fires (a 300ms debounce could otherwise land after the
  // attempt is voided).
  const cancelPendingSubmit = useCallback(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);
  cancelPendingSubmitRef.current = cancelPendingSubmit;

  const queueTimedSubmit = useCallback(
    (qIndex: number, ids: string[]) => {
      // L-2: do not queue saves once auto-voided.
      if (voidedRef.current) return;
      const q = view.questions[qIndex];
      if (!q) return;
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        if (voidedRef.current) return;
        submitMutation.mutate(
          { questionId: q.id, selectedOptionIds: ids },
          {
            onError: (err) => {
              toast(
                err instanceof ApiError ? err.message : 'Could not save answer.',
                { variant: 'error' },
              );
            },
          },
        );
      }, 300);
    },
    [submitMutation, toast, view.questions],
  );

  // ----- Selection change handler -----------------------------------------
  const handleSelectionChange = (next: string[]) => {
    // L-2: a voided attempt accepts no further input client-side.
    if (integrity.voided) return;
    if (!current) return;
    const idx = view.currentIndex;
    setView((prev) => {
      const arr = [...prev.questions];
      const cur = arr[idx];
      if (!cur) return prev;
      arr[idx] = { ...cur, selectedOptionIds: next, feedback: null };
      return { ...prev, questions: arr };
    });

    if (view.mode === 'timed_exam') {
      queueTimedSubmit(idx, next);
    }
    // practice mode: user must press "Submit answer" to commit
  };

  const handlePracticeSubmit = () => {
    if (!current) return;
    if (current.selectedOptionIds.length === 0) {
      toast('Pick an option first.', { variant: 'info' });
      return;
    }
    void submitPracticeAnswer(view.currentIndex, current.selectedOptionIds);
  };

  // ----- Complete attempt --------------------------------------------------
  const completeMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ status: string }>(
        `/api/assessments/attempts/${encodeURIComponent(attemptId)}/complete`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      try {
        window.sessionStorage.removeItem(positionStorageKey(attemptId));
      } catch {
        // ignore
      }
      void navigate({
        to: '/assessments/attempts/$attemptId/result',
        params: { attemptId },
      });
    },
    onError: (err: unknown) => {
      toast(
        err instanceof ApiError ? err.message : 'Could not submit attempt.',
        { variant: 'error' },
      );
    },
  });

  const onSubmitAll = async () => {
    // L-2: a voided attempt cannot be submitted by the candidate.
    if (integrity.voided) return;
    const unanswered = view.questions.filter(
      (q) => q.selectedOptionIds.length === 0 && !q.finalized,
    ).length;
    const ok = await confirm({
      title: 'Submit your test?',
      description:
        unanswered > 0
          ? `You have ${unanswered} unanswered question${unanswered === 1 ? '' : 's'}. Submit anyway?`
          : 'You can no longer change your answers after submission.',
      confirmLabel: 'Submit',
      variant: unanswered > 0 ? 'danger' : 'default',
    });
    if (!ok) return;
    completeMutation.mutate();
  };

  const onExpired = useCallback(() => {
    // Belt-and-braces finalisation. The Timer fires this exactly once (it
    // guards with an internal expiredFiredRef across both the local tick and
    // the heartbeat), so we get a single toast and a single /complete call.
    // We reuse the existing completeMutation so we share its sessionStorage
    // cleanup and audit trail rather than duplicating that logic.
    //
    // onSettled (not onSuccess) guarantees navigation whether /complete
    // succeeds or fails: the result-page GET calls ensureLatestAttemptState()
    // as a server-side backstop finaliser, so a transient /complete error must
    // never strand the candidate on the question screen.
    //
    // We depend on completeMutation.mutate (a stable reference) rather than the
    // whole mutation object so onExpired stays referentially stable — the Timer
    // lists onExpired in its tick/heartbeat effect deps, and an unstable
    // reference would needlessly tear down and recreate those intervals on
    // every AttemptRunner re-render.
    toast('Time is up — submitting your answers now…', { variant: 'info' });
    completeMutation.mutate(undefined, {
      onSettled: () => {
        void navigate({
          to: '/assessments/attempts/$attemptId/result',
          params: { attemptId },
        });
      },
    });
  }, [completeMutation.mutate, navigate, attemptId, toast]);

  // ----- Layout -----------------------------------------------------------
  // L-2: when the anti-cheat threshold voids the attempt in-session, swap the
  // entire question UI (including the Timer, which unmounts and stops its
  // heartbeat) for the terminal panel. This is the hard client-side stop.
  if (integrity.voided) {
    return <VoidedPanel reason="tab_switch" />;
  }

  if (!current) {
    return (
      <div className="px-4 py-8 text-sm text-[color:var(--color-text-muted)]">
        No questions to display.
      </div>
    );
  }

  // Multi-select detection: the server-served question carries this as a
  // single_choice type today, but the validation contract allows multiple
  // correct ids. We infer from the available options; the server enforces.
  const multiSelect = false;

  return (
    <div className="flex min-h-[calc(100vh-3rem)] flex-col bg-[color:var(--color-bg)]">
      {/* ---------- Sticky top bar ---------- */}
      <header className="sticky top-0 z-10 border-b border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]/95 backdrop-blur">
        <div className="mx-auto max-w-4xl px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {view.scope ? <ScopeChip scope={view.scope} /> : null}
              <ModeChip mode={view.mode} />
            </div>
            <Timer
              attemptId={attemptId}
              expiresAt={view.expiresAt}
              initialSecondsRemaining={view.initialSecondsRemaining}
              onExpired={onExpired}
            />
          </div>
          <div className="mt-3">
            <QuestionNavigator
              total={totalQuestions}
              currentIndex={view.currentIndex}
              answered={answeredSet}
              onJump={(idx) =>
                setView((prev) => ({ ...prev, currentIndex: idx }))
              }
            />
          </div>
        </div>
      </header>

      {/* ---------- Main question area ---------- */}
      <main className="flex-1 px-4 py-8">
        {/* L-2: persistent anti-cheat warning. Once the candidate crosses the
            flag threshold this banner stays on screen (it is not a one-shot
            toast) so they understand the cost of switching away again. */}
        {integrity.flagged ? (
          <div
            role="alert"
            aria-live="assertive"
            className="mx-auto mb-6 flex max-w-3xl items-start gap-3 rounded-xl border border-[color:var(--color-danger)] bg-[color:var(--color-danger)]/10 px-4 py-3"
          >
            <ShieldAlert
              size={20}
              className="mt-0.5 shrink-0 text-[color:var(--color-danger)]"
              aria-hidden="true"
            />
            <div className="text-sm">
              <p className="font-semibold text-[color:var(--color-danger)]">
                You have been flagged for switching away from the test
              </p>
              <p className="mt-0.5 text-[color:var(--color-text-muted)]">
                {integrity.count} switch{integrity.count === 1 ? '' : 'es'} recorded.
                Continuing to leave the test window will end your test
                automatically. Please stay on this page until you finish.
              </p>
            </div>
          </div>
        ) : null}

        <article className="mx-auto max-w-3xl">
          <QuestionRenderer
            position={current.position}
            totalPositions={totalQuestions}
            stem={current.stem}
            options={current.options}
            selectedOptionIds={current.selectedOptionIds}
            multiSelect={multiSelect}
            disabled={current.finalized}
            feedback={view.mode === 'practice' ? current.feedback : null}
            onChange={handleSelectionChange}
          />

          {view.mode === 'practice' ? (
            <PracticeFooter
              question={current}
              onSubmitAnswer={handlePracticeSubmit}
            />
          ) : null}
        </article>
      </main>

      {/* ---------- Bottom navigation bar ---------- */}
      <footer className="sticky bottom-0 z-10 border-t border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]/95 backdrop-blur">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <Button
            variant="ghost"
            size="md"
            icon={<ChevronLeft size={16} aria-hidden="true" />}
            disabled={view.currentIndex === 0}
            onClick={() =>
              setView((prev) => ({
                ...prev,
                currentIndex: Math.max(0, prev.currentIndex - 1),
              }))
            }
          >
            Previous
          </Button>
          <div className="text-xs text-[color:var(--color-text-muted)]">
            Question {view.currentIndex + 1} of {totalQuestions}
          </div>
          {view.currentIndex < totalQuestions - 1 ? (
            <Button
              variant="secondary"
              size="md"
              onClick={() =>
                setView((prev) => ({
                  ...prev,
                  currentIndex: Math.min(
                    totalQuestions - 1,
                    prev.currentIndex + 1,
                  ),
                }))
              }
            >
              Next
              <ChevronRight size={16} aria-hidden="true" />
            </Button>
          ) : (
            <Button
              size="md"
              icon={<Send size={16} aria-hidden="true" />}
              onClick={onSubmitAll}
              loading={completeMutation.isPending}
            >
              Submit attempt
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Practice footer: explicit "Submit answer" + retry affordance
// ---------------------------------------------------------------------------

interface PracticeFooterProps {
  question: AttemptViewQuestion;
  onSubmitAnswer: () => void;
}

function PracticeFooter({ question, onSubmitAnswer }: PracticeFooterProps) {
  if (question.finalized) {
    return (
      <div className="mx-auto mt-6 max-w-3xl">
        <div
          className="flex items-center gap-2 rounded-md border border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 px-4 py-3 text-sm font-medium text-[color:var(--color-success)]"
          role="status"
        >
          <CheckCircle2 size={16} aria-hidden="true" />
          Correct! Moving on…
        </div>
      </div>
    );
  }
  if (question.feedback === 'wrong') {
    return (
      <div className="mx-auto mt-6 max-w-3xl space-y-3">
        <div
          className="flex items-center gap-2 rounded-md border border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/10 px-4 py-3 text-sm text-[color:var(--color-danger)]"
          role="status"
        >
          <AlertCircle size={16} aria-hidden="true" />
          That wasn't quite right — give it another try.
        </div>
        <Button
          variant="secondary"
          size="md"
          icon={<RefreshCw size={16} aria-hidden="true" />}
          onClick={onSubmitAnswer}
          disabled={question.selectedOptionIds.length === 0}
        >
          Submit answer
        </Button>
        {question.attemptCount > 0 ? (
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Attempts on this question so far: {question.attemptCount}
          </p>
        ) : null}
      </div>
    );
  }
  return (
    <div className="mx-auto mt-6 max-w-3xl">
      <Button
        size="md"
        icon={<Send size={16} aria-hidden="true" />}
        onClick={onSubmitAnswer}
        disabled={question.selectedOptionIds.length === 0}
      >
        Submit answer
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// State helpers
// ---------------------------------------------------------------------------

const buildInitialView = (
  attemptId: string,
  detail: AttemptDetail,
): AttemptViewState => {
  const questions: AttemptViewQuestion[] = detail.answers.map((a) => ({
    id: a.questionId,
    position: a.questionPosition,
    stem: a.stem,
    options: a.options,
    attemptCount: a.attemptCount,
    finalized: a.status === 'finalized',
    selectedOptionIds: a.selectedOptionIds ?? [],
    feedback: null,
  }));

  let storedIndex = 0;
  try {
    const raw = window.sessionStorage.getItem(positionStorageKey(attemptId));
    if (raw !== null) {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0 && n < questions.length) {
        storedIndex = n;
      }
    }
  } catch {
    // ignore
  }

  // If the user has been mid-attempt and we have no stored index, jump to the
  // first unanswered question (better resume UX than starting from #1).
  let initialIndex = storedIndex;
  if (storedIndex === 0) {
    const firstUnanswered = questions.findIndex(
      (q) => q.selectedOptionIds.length === 0 && !q.finalized,
    );
    if (firstUnanswered > 0) initialIndex = firstUnanswered;
  }

  return {
    attemptId,
    runId: detail.runId,
    templateId: detail.templateId,
    mode: detail.mode,
    scope: detail.scope,
    expiresAt: detail.expiresAt,
    initialSecondsRemaining:
      detail.expiresAt && detail.status === 'in_progress'
        ? detail.secondsRemaining
        : null,
    // Anti-cheat is conservatively gated on the `hiring` scope, which is the
    // only scope wired to `hiring_strict` in the spec today. The attempt
    // detail endpoint does not return the antiCheatProfile (the candidate
    // role can't read templates), so we use the scope as a faithful proxy.
    antiCheatStrict: detail.scope === 'hiring',
    questions,
    currentIndex: initialIndex,
  };
};

// Merge a fresh detail payload into an existing view without clobbering the
// user's in-flight selection on the current question. Used when React Query
// refetches on focus/reconnect.
const mergeDetail = (
  prev: AttemptViewState,
  detail: AttemptDetail,
): AttemptViewState => {
  // If we don't yet have an attempt or this is a totally new attemptId, just
  // rebuild from scratch.
  if (prev.attemptId !== detail.id) {
    return buildInitialView(detail.id, detail);
  }

  const newQuestions: AttemptViewQuestion[] = detail.answers.map((a) => {
    const existing = prev.questions.find((q) => q.id === a.questionId);
    if (!existing) {
      return {
        id: a.questionId,
        position: a.questionPosition,
        stem: a.stem,
        options: a.options,
        attemptCount: a.attemptCount,
        finalized: a.status === 'finalized',
        selectedOptionIds: a.selectedOptionIds ?? [],
        feedback: null,
      };
    }
    // Preserve the user's in-flight selection unless the server has finalised
    // the question — in which case the server wins.
    return {
      ...existing,
      attemptCount: Math.max(existing.attemptCount, a.attemptCount),
      finalized: a.status === 'finalized' || existing.finalized,
      // If the server says we have no selection (e.g. practice wrong → cleared),
      // honour it.
      selectedOptionIds:
        a.status === 'finalized'
          ? a.selectedOptionIds ?? existing.selectedOptionIds
          : existing.selectedOptionIds.length > 0
            ? existing.selectedOptionIds
            : a.selectedOptionIds ?? [],
    };
  });

  return {
    ...prev,
    expiresAt: detail.expiresAt,
    initialSecondsRemaining:
      detail.expiresAt && detail.status === 'in_progress'
        ? detail.secondsRemaining
        : prev.initialSecondsRemaining,
    questions: newQuestions,
  };
};

// We keep this helper around so future hiring-strict feature toggles can
// import without needing to crack open the heavy AttemptRunner file.
export function isAttemptStartShape(value: unknown): value is AttemptStart {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.attemptId === 'string' && Array.isArray(v.questions);
}
